import React from 'react'

import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'
import { Features } from 'generated/featureDefinitions'
import {
  FinanceOverviewBankAccountsQuery,
  TreasuryOverviewPageDocument,
  TreasuryOverviewPageQuery,
} from 'generated/resources/graphql'

import { DateNavigator } from 'modules/core/components/DateNavigator'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { buildBankAccountsTransactionsDashboardWidget } from 'modules/banking/components/Widgets/BankAccountsTransactionsWidget'
import {
  BankAccountsPreparedData,
  buildBankAccountsListWidget,
} from 'modules/banking/components/Widgets/BankAccountsWidget'
import { buildPaymentsDistributionDashboardWidget } from 'modules/banking/components/Widgets/PaymentsDistributionWidget'
import { buildPaymentsOverdueDashboardWidget } from 'modules/banking/components/Widgets/PaymentsOverdueWidget'
import { buildPaymentRequestsOverdueDashboardWidget } from 'modules/banking/components/Widgets/PaymentRequestsOverdueWidget'
import { buildPaymentsStatusDashboardWidget } from 'modules/banking/components/Widgets/PaymentsStatusWidget'
import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { UpsellModuleBanner } from 'modules/finance/components/Overview/_UpsellModuleBanner'
import { useOverviewPage } from 'modules/finance/components/Overview/common'
import { useTreasuryDateRange } from 'modules/finance/hooks/useTreasuryDateRange'
import { buildPendingActionsWidget } from 'modules/finance/components/Widgets/PendingActionsWidget'
import { WidgetDashboard, WidgetParams } from 'modules/finance/components/Widgets/common/widgets'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import FinanceOverviewBetaAdvisoryBanner from './_FinanceOverviewBetaAdvisoryBanner'

const paymentWidgets = (
  locale: string,
  dashboardFilters: { endDate: string; startDate: string; currency: string },
  widgetVisibilityChecks: {
    showPaymentWidgets: boolean
    showBankingWidgets: boolean
    showPaymentRequestsWidgets: boolean
  }
) => {
  if (!widgetVisibilityChecks.showPaymentWidgets && !widgetVisibilityChecks.showBankingWidgets) {
    return []
  }

  const { currency, endDate, startDate } = dashboardFilters
  const amountFormatter = (value: unknown) =>
    formattedAmount({
      amountCents: Number(value),
      currency: dashboardFilters.currency,
      locale: locale,
    })

  return [
    widgetVisibilityChecks.showBankingWidgets &&
      buildBankAccountsTransactionsDashboardWidget({
        filters: {
          currency: { eq: dashboardFilters.currency },
          bookingDate: { between: [startDate, endDate] },
        },
        prepareDataArgs: {
          amountFormatter,
          displayedFilters: [],
        },
      }),
    // FF on: new PaymentsStatus widget with dueDate filter (replaces Distribution)
    widgetVisibilityChecks.showPaymentWidgets &&
      widgetVisibilityChecks.showPaymentRequestsWidgets &&
      buildPaymentsStatusDashboardWidget({
        queryArgs: {
          currency,
          paymentsFilter: {
            dueDate: { between: [startDate, endDate] },
          },
        },
        prepareDataArgs: {
          amountFormatter,
          displayedFilters: [],
        },
      }),
    // FF off: original Distribution widget with date (creation date) filter
    widgetVisibilityChecks.showPaymentWidgets &&
      !widgetVisibilityChecks.showPaymentRequestsWidgets &&
      buildPaymentsDistributionDashboardWidget({
        queryArgs: {
          currency,
          paymentsFilters: {
            date: { between: [startDate, endDate] },
          },
          transfersFilters: {
            createdAt: { between: [startDate, endDate] },
          },
        },
        prepareDataArgs: {
          amountFormatter,
          displayedFilters: [],
        },
      }),
    // FF off: Overdue widget (hidden when FF on since PaymentsStatus covers it)
    widgetVisibilityChecks.showPaymentWidgets &&
      !widgetVisibilityChecks.showPaymentRequestsWidgets &&
      buildPaymentsOverdueDashboardWidget({
        queryArgs: {
          currency,
          paymentsFilters: {
            date: { between: [startDate, endDate] },
          },
          transfersFilters: {
            createdAt: { between: [startDate, endDate] },
          },
        },
        prepareDataArgs: {
          amountFormatter,
          displayedFilters: [],
        },
      }),
    widgetVisibilityChecks.showPaymentRequestsWidgets &&
      buildPaymentRequestsOverdueDashboardWidget({
        queryArgs: {
          prFilter: { dueDate: { between: [startDate, endDate] } },
        },
        prepareDataArgs: {
          amountFormatter,
          displayedFilters: [],
        },
      }),
  ].filter(Boolean)
}

const generalWidgets = (
  locale: string,
  widgetVisibilityChecks: ReturnType<typeof buildWidgetVisibilityChecks>
): WidgetParams<
  FinanceOverviewBankAccountsQuery,
  BankAccountsPreparedData,
  { endDate: string; startDate: string }
>[] => {
  const amountFormatter = (value: unknown, currency: string) =>
    formattedAmount({
      amountCents: Number(value),
      currency,
      locale: locale,
    })

  return [
    buildPendingActionsWidget(['treasury']),
    widgetVisibilityChecks.showBankingWidgets &&
      buildBankAccountsListWidget({
        prepareDataArgs: { amountFormatter },
      }),
  ].filter(Boolean)
}

const buildWidgetVisibilityChecks = (
  data?: TreasuryOverviewPageQuery,
  hasPaymentRequests = false
) => {
  const canManagePayment =
    data?.finance?._policies?.sidebarSeeFinancePaymentsSubLevelItem?.can ?? false
  const canSeeBankAccounts =
    data?.finance?._policies?.sidebarSeeFinanceAccountsSubLevelItem?.can ?? false

  return {
    showBankingWidgets: canSeeBankAccounts,
    showPaymentWidgets: canManagePayment,
    showPaymentRequestsWidgets: canManagePayment && hasPaymentRequests,
  }
}

const TreasuryOverviewPage = () => {
  const { data, loading, error } = useQuery(TreasuryOverviewPageDocument, {
    variables: {
      featureIds: [
        Features.accounts_payable_management,
        Features.expenses_core,
        Features.procurement_core,
        Features.it_management_management,
      ],
    },
  })

  const canAccessOverview =
    data?.finance._policies.sidebarSeeFinanceOverviewSubLevelItem?.can ?? false

  const { locale, missingModule, moduleUpsellBannerHidden, setModuleUpsellBannerHidden } =
    useOverviewPage(data?.companies)

  const { company } = useCurrentSession()
  const {
    startDate: sharedStartDate,
    endDate: sharedEndDate,
    onDateRangeChange: setSharedDateRange,
  } = useTreasuryDateRange()

  const interval = { startDate: sharedStartDate, endDate: sharedEndDate }
  const onDateRangeChange = React.useCallback(
    (start: Date, end: Date) => {
      setSharedDateRange(start, end)
      track(trackingEvents.accounting.overview.dateNavigationFilterChanged, {
        start_date: start.toISOString(),
        end_date: end.toISOString(),
      })
    },
    [setSharedDateRange]
  )
  const dashboardFilters = React.useMemo(
    () => ({
      startDate: moment(sharedStartDate).format('YYYY-MM-DD'),
      endDate: moment(sharedEndDate).format('YYYY-MM-DD'),
      currency: company?.legalEntities.find((el) => el.main)?.currency ?? 'EUR',
    }),
    [sharedStartDate, sharedEndDate, company?.legalEntities]
  )

  const { has_dev_banking_payment_requests } = useFeatures(['dev_banking_payment_requests'])
  const widgetVisibilityChecks = buildWidgetVisibilityChecks(data, has_dev_banking_payment_requests)

  useUnsafeEffect(() => {
    if (!loading && data && canAccessOverview) {
      const checks = widgetVisibilityChecks
      track(trackingEvents.accounting.overview.sectionView, {
        show_banking_widgets: checks.showBankingWidgets,
        show_payment_widgets: checks.showPaymentWidgets,
      })
    }
  }, [loading, data, canAccessOverview, widgetVisibilityChecks])

  return (
    <ResponsivePage.Body
      variant="full"
      error={error}
      loading={loading}
      authorized={canAccessOverview}
    >
      <Box flexDirection="column" gap="s32">
        {missingModule && !moduleUpsellBannerHidden && (
          <UpsellModuleBanner
            content={i18n.t('finance.overview.upsell.banner.description')}
            title={i18n.t('finance.overview.upsell.banner.title')}
            missingModule={missingModule.module}
            missingFeature={missingModule.feature}
            onHide={() => setModuleUpsellBannerHidden(true)}
            source="ap_treasury_overview_upsell_card"
          />
        )}

        <WidgetDashboard widgets={generalWidgets(locale, widgetVisibilityChecks)} />
        <Divider />
        <DateNavigator
          startDate={interval.startDate}
          endDate={interval.endDate}
          onDateRangeChange={onDateRangeChange}
        />
        <FinanceOverviewBetaAdvisoryBanner />
        <Box flexDirection="column" gap="s48">
          <Box height="full" gap="s8">
            <Text.Header2 title={i18n.t('finance.overview.banks.section_title')}>
              {i18n.t('finance.overview.banks.section_title')}
            </Text.Header2>
            <Box padding="s8">
              <WidgetDashboard
                widgets={paymentWidgets(locale, dashboardFilters, widgetVisibilityChecks)}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </ResponsivePage.Body>
  )
}

export default TreasuryOverviewPage
